import React, { useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import EmailQuoteDialog from './EmailQuoteDialog';

/**
 * Audiobook production pricing estimator.
 *
 * Jo charges £60/hour for our production work (edit, master, chapter
 * markers, ACX submission, Audible/Apple approval). What varies book-to-
 * book is how many production hours a "finished hour" of audio actually
 * takes.
 *
 * Rules of thumb signed off by Jo (Feb 2026):
 *   - Author-narrated, raw audio: ~5 production hours per finished hour
 *     (more cleanup, breath removal, retake weaving, tone matching)
 *   - Pro-narrated, clean audio:  ~3 production hours per finished hour
 *     (studio-clean input, mostly QA + mastering + ACX prep)
 *
 * If the user asks for a rush turnaround inside 3 weeks, add 50%. Same
 * 25% band as the book estimator so users see a range, not a point.
 */
const RATE_PER_HOUR = 60;
const RANGE_MULTIPLIER = 1.25;
const RUSH_MULTIPLIER = 1.5;

const NARRATOR_ROUTES = [
  {
    id: 'author',
    label: 'Author-narrated',
    hint: 'You record, we edit + master',
    prodHoursPerFinishedHour: 5,
  },
  {
    id: 'pro',
    label: 'Pro narrator',
    hint: 'We cast + direct, they deliver clean audio',
    prodHoursPerFinishedHour: 3,
    passThroughNote: 'Narrator fee passed through at cost, no markup.',
  },
];

const PRESETS = [
  { id: 'business-book',   label: 'Business book',   hint: '4 finished hours',  finishedHours: 4,  narrator: 'author' },
  { id: 'memoir',          label: 'Memoir',          hint: '6 finished hours',  finishedHours: 6,  narrator: 'author' },
  { id: 'full-novel',      label: 'Full novel',      hint: '10 finished hours', finishedHours: 10, narrator: 'pro'    },
];

function gbp(n) {
  return `£${Math.round(n).toLocaleString()}`;
}

export default function AudiobookEstimator({ testId = 'audiobook-estimator' }) {
  const [finishedHours, setFinishedHours] = useState(6);
  const [narratorId, setNarratorId] = useState('author');
  const [rush, setRush] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const applyPreset = (preset) => {
    setFinishedHours(preset.finishedHours);
    setNarratorId(preset.narrator);
    setActivePreset(preset.id);
  };

  const { low, high, prodHours, route } = useMemo(() => {
    const r = NARRATOR_ROUTES.find((n) => n.id === narratorId);
    const hours = finishedHours * r.prodHoursPerFinishedHour;
    const base = hours * RATE_PER_HOUR;
    const rushed = rush ? base * RUSH_MULTIPLIER : base;
    return {
      low: rushed,
      high: rushed * RANGE_MULTIPLIER,
      prodHours: hours,
      route: r,
    };
  }, [finishedHours, narratorId, rush]);

  return (
    <div
      className="bg-white rounded-[16px] border border-[#E2E8F0] p-6 sm:p-8 shadow-[0_18px_40px_-24px_rgba(30,42,68,0.35)]"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display font-semibold text-[#1E2A44] text-xl sm:text-2xl">
          Estimate your audiobook
        </h3>
        <p className="text-xs uppercase tracking-widest text-[#94A3B8]">Indicative ballpark</p>
      </div>
      <p className="text-[#475569] mt-2 text-sm leading-relaxed">
        We charge £60/hour for the production side (edit, master, chapter markers, ACX submission,
        Audible / Apple approval). What varies book-to-book is how many production hours a finished
        hour of audio needs. Pick a starting point, or set your own.
      </p>

      {/* Presets */}
      <div className="mt-5" data-testid={`${testId}-presets`}>
        <p className="text-[#1E2A44] font-semibold text-sm mb-2">Start from a typical audiobook</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const isActive = activePreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                aria-pressed={isActive}
                className={`text-left rounded-[10px] border px-3.5 py-2.5 transition-all ${
                  isActive
                    ? 'border-[#BC516F] bg-[#BC516F] text-white shadow-[0_6px_16px_-6px_rgba(188,81,111,0.55)]'
                    : 'border-[#E2E8F0] bg-white text-[#1E2A44] hover:border-[#BC516F] hover:bg-[#FFF3F6]'
                }`}
                data-testid={`${testId}-preset-${preset.id}`}
              >
                <div className="font-semibold text-sm leading-tight">{preset.label}</div>
                <div className={`text-xs mt-0.5 ${isActive ? 'text-white/85' : 'text-[#64748B]'}`}>{preset.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Finished hours slider */}
      <div className="mt-6">
        <div className="flex justify-between items-baseline">
          <label htmlFor={`${testId}-hours`} className="text-[#1E2A44] font-semibold text-sm">
            Finished hours of audio
          </label>
          <span className="font-display text-[#BC516F] font-bold text-lg" data-testid={`${testId}-hours-value`}>
            {finishedHours} {finishedHours === 1 ? 'hour' : 'hours'}
          </span>
        </div>
        <input
          id={`${testId}-hours`}
          type="range"
          min="2"
          max="16"
          step="1"
          value={finishedHours}
          onChange={(e) => { setFinishedHours(parseInt(e.target.value, 10)); setActivePreset(null); }}
          className="w-full mt-2 accent-[#BC516F]"
          data-testid={`${testId}-hours-range`}
        />
        <div className="flex justify-between text-xs text-[#94A3B8] mt-1">
          <span>2h</span><span>6h (business)</span><span>10h (novel)</span><span>16h</span>
        </div>
      </div>

      {/* Narrator route */}
      <div className="mt-6">
        <p className="text-[#1E2A44] font-semibold text-sm">Who's narrating?</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {NARRATOR_ROUTES.map((r) => {
            const active = narratorId === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => { setNarratorId(r.id); setActivePreset(null); }}
                aria-pressed={active}
                className={`text-left rounded-[10px] border p-3 transition-all ${
                  active
                    ? 'border-[#BC516F] bg-[#FFF3F6]'
                    : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
                }`}
                data-testid={`${testId}-route-${r.id}`}
              >
                <p className="font-semibold text-[#1E2A44] text-sm leading-tight">{r.label}</p>
                <p className="text-[#475569] text-xs mt-0.5">{r.hint}</p>
                <p className="text-[#94A3B8] text-xs mt-1">~{r.prodHoursPerFinishedHour} production hrs / finished hr</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Rush toggle */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => { setRush(!rush); setActivePreset(null); }}
          aria-pressed={rush}
          className={`w-full text-left rounded-[10px] border p-3 transition-all ${
            rush
              ? 'border-[#BC516F] bg-[#FFF3F6]'
              : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
          }`}
          data-testid={`${testId}-rush`}
        >
          <div className="flex items-center gap-2.5">
            <span className={`w-5 h-5 rounded-[6px] flex-shrink-0 ${rush ? 'bg-[#BC516F]' : 'bg-[#F1F5F9]'}`} />
            <div>
              <p className="font-semibold text-[#1E2A44] text-sm">Rush turnaround (inside 3 weeks)</p>
              <p className="text-[#475569] text-xs">Adds 50% to production hours</p>
            </div>
          </div>
        </button>
      </div>

      {/* Result */}
      <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#94A3B8]">Indicative ballpark</p>
          <p className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl mt-1" data-testid={`${testId}-total`}>
            {gbp(low)} <span className="text-[#94A3B8] font-normal text-xl">–</span> {gbp(high)}
          </p>
          <p className="text-[#475569] text-xs mt-1">
            Roughly {Math.round(prodHours * (rush ? RUSH_MULTIPLIER : 1))} production hours at £60/hr.
            {route.passThroughNote && ` ${route.passThroughNote}`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            className="inline-flex items-center justify-center gap-2 border-2 border-[#BC516F] text-[#BC516F] hover:bg-[#FFF3F6] font-semibold px-4 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-email-me`}
          >
            <Mail className="w-4 h-4" /> Email me this quote
          </button>
          <a
            href="https://calendly.com/audioandco/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#A7468A] text-white font-semibold px-5 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-cta`}
          >
            Get an exact quote
          </a>
        </div>
      </div>

      <EmailQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        testId={`${testId}-email`}
        getPayload={() => {
          const rushMultiplierApplied = rush ? RUSH_MULTIPLIER : 1;
          return {
            service: 'audiobook',
            total_label: `${gbp(low)} – ${gbp(high)}`,
            line_items: [
              { label: 'Finished hours of audio', detail: `${finishedHours} ${finishedHours === 1 ? 'hour' : 'hours'}`, amount: '' },
              { label: 'Narrator route',          detail: route.label, amount: `${route.prodHoursPerFinishedHour} prod hrs / finished hr` },
              { label: 'Production hours',        detail: `${Math.round(prodHours * rushMultiplierApplied)} hrs at £${RATE_PER_HOUR}/hr`, amount: '' },
              ...(rush ? [{ label: 'Rush turnaround (inside 3 weeks)', detail: '+50% to production hours', amount: '' }] : []),
            ],
            notes: (route.passThroughNote || '') + ' Production billed hourly, exact quote confirmed on the free call.',
          };
        }}
      />
    </div>
  );
}
